// CrowdInsights.jsx
import { useMemo, useCallback, useRef, useEffect, useState } from "react";
import Layout from "./Layout.js";
import {
  GoogleMap,
  HeatmapLayer,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import { collection, onSnapshot, db } from "../ServiceConnection/firebase.js";
import { getDistance } from "geolib";

const DEFAULT_CENTER = { lat: 23.285, lng: 77.275 };
const DEFAULT_ZOOM = 17;
const MAP_CONTAINER_STYLE = { width: "100%", height: "620px" };
const LIBRARIES = ["visualization"];
const priorityRank = { High: 3, Medium: 2, Low: 1 };

export default function CrowdInsights() {
  const mapRef = useRef(null);
  const [crowdPoints, setCrowdPoints] = useState([]);
  const [processedPoints, setProcessedPoints] = useState([]);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "script-loader",
    googleMapsApiKey: "AIzaSyC3WmMY1uBgZmeiAPftovjM_tY0vbUi3Zs",
    libraries: LIBRARIES,
    language: "en",
    region: "US",
  });

  // ✅ Firebase Realtime Listener
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "crowd_locations"), (snapshot) => {
      const data = snapshot.docs.map((doc) => doc.data());
      console.log("🔥 Firestore Data:", data);
      setCrowdPoints(data);
    });
    return () => unsub();
  }, []);

  // ✅ Process crowd data with nearby count
  useEffect(() => {
    if (crowdPoints.length === 0) {
      setProcessedPoints([]);
      return;
    }

    const processed = crowdPoints.map((loc) => {
      const lat = parseFloat(loc.lat);
      const lng = parseFloat(loc.lng);

      // Count nearby points within 2km
      const countNearby = crowdPoints.filter((p) => {
        const plat = parseFloat(p.lat);
        const plng = parseFloat(p.lng);
        return (
          getDistance(
            { latitude: lat, longitude: lng },
            { latitude: plat, longitude: plng }
          ) <= 2000
        );
      }).length;

      // Determine priority based on crowd density
      let priority = "Low";
      if (countNearby > 10) priority = "High";
      else if (countNearby > 5) priority = "Medium";

      // Determine trend (you can enhance this with historical data)
      let trend = "steady";
      if (countNearby > 10) trend = "up";
      else if (countNearby < 3) trend = "down";

      // Calculate ETA (example logic - customize as needed)
      const etaMins = Math.max(15, 60 - countNearby * 3);

      return {
        lat,
        lng,
        label: loc.label || "Unknown Location",
        priority,
        etaMins,
        trend,
        countNearby,
      };
    });

    console.log("✅ Processed Points:", processed);
    setProcessedPoints(processed);
  }, [crowdPoints]);

  const heatmapData = useMemo(() => {
    if (!isLoaded || !window.google || processedPoints.length === 0) return [];
    return processedPoints.map((p) => ({
      location: new window.google.maps.LatLng(p.lat, p.lng),
      weight: p.priority === "High" ? 5 : p.priority === "Medium" ? 3 : 1,
    }));
  }, [isLoaded, processedPoints]);

  const fitToPoints = useCallback(
    (map) => {
      if (!window.google || !map || processedPoints.length === 0) return;
      const bounds = new window.google.maps.LatLngBounds();
      processedPoints.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      if (!bounds.isEmpty()) map.fitBounds(bounds);
      else {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(DEFAULT_ZOOM);
      }
    },
    [processedPoints]
  );

  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      if (processedPoints.length > 0) fitToPoints(map);
      else {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(DEFAULT_ZOOM);
      }
    },
    [fitToPoints, processedPoints]
  );

  const vizOK = !!window.google?.maps?.visualization;

  // Find the most urgent hotspot
  const urgent = useMemo(() => {
    if (!processedPoints.length) return null;
    const sorted = [...processedPoints].sort((a, b) => {
      const pr = priorityRank[b.priority] - priorityRank[a.priority];
      if (pr !== 0) return pr;
      return (a.etaMins ?? 9999) - (b.etaMins ?? 9999);
    });
    return sorted[0];
  }, [processedPoints]);

  // 🔥 Color + icon for crowd trend
  const trendVisual = (trend) => {
    switch (trend) {
      case "up":
        return (
          <span style={{ color: "#e53935", fontWeight: 700 }}>▲ Rising</span>
        );
      case "down":
        return (
          <span style={{ color: "#43a047", fontWeight: 700 }}>▼ Easing</span>
        );
      default:
        return (
          <span style={{ color: "#757575", fontWeight: 700 }}>→ Steady</span>
        );
    }
  };

  const bgByPriority = (p) =>
    p === "High" ? "#ff4444" : p === "Medium" ? "#ffcc33" : "#4a90e2";

  return (
    <Layout>
      <div className="page">
        <h2 className="page-title">Crowd Heatmap</h2>

        {/* 🔔 Urgent banner */}
        {urgent && (
          <div
            style={{
              marginBottom: 10,
              padding: "10px 14px",
              borderRadius: 8,
              background: "#fff5f5",
              border: "1px solid #ffd4d4",
              color: "#b00020",
              fontWeight: 600,
              display: "inline-block",
              fontSize: "15px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            }}
          >
            ⚠️ {urgent.label} may be overcrowded in {urgent.etaMins} mins (
            {urgent.priority}) —{" "}
            <span style={{ marginLeft: 4 }}>{trendVisual(urgent.trend)}</span>
          </div>
        )}

        <div style={{ width: "100%", minHeight: 560, position: "relative" }}>
          {loadError && (
            <div className="text-red-600 mt-4">
              Map failed to load: {String(loadError?.message || loadError)}
            </div>
          )}

          {!isLoaded && !loadError && (
            <div className="mt-4">Loading Google Map…</div>
          )}

          {isLoaded && (
            <GoogleMap
              onLoad={onMapLoad}
              mapContainerStyle={MAP_CONTAINER_STYLE}
              options={{
                mapTypeId: "roadmap",
                streetViewControl: false,
                fullscreenControl: false,
              }}
            >
              {/* Heatmap Layer */}
              {vizOK && heatmapData.length > 0 && (
                <HeatmapLayer
                  data={heatmapData}
                  options={{
                    radius: 35,
                    opacity: 0.85,
                    dissipating: true,
                  }}
                />
              )}

              {/* Label overlays */}
              {processedPoints.map((p, i) => (
                <OverlayView
                  key={`label-${i}`}
                  position={{ lat: p.lat, lng: p.lng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      background: bgByPriority(p.priority),
                      color: "#000",
                      padding: "6px 8px 4px 8px",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: 700,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                      whiteSpace: "nowrap",
                      transform: "translate(-50%, -130%)",
                      border: "1px solid rgba(0,0,0,0.2)",
                    }}
                  >
                    <div>{p.label}</div>
                    <div
                      style={{
                        marginTop: 2,
                        fontSize: "11px",
                        fontWeight: 600,
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <span>🕒 {p.etaMins} mins</span>
                      {trendVisual(p.trend)}
                    </div>
                  </div>
                </OverlayView>
              ))}
            </GoogleMap>
          )}
        </div>
      </div>
    </Layout>
  );
}
